import { keyframes } from '@emotion/react';

/**
 * Ocean design tokens — the single source for the palette, gradients and
 * keyframes used by the dashboards. Import from here rather than re-declaring
 * hex values or animations in a page.
 */

export const OCEAN = {
  navy: '#0C4A6E',
  ocean: '#0891B2',
  aqua: '#22D3EE',
  sky: '#38BDF8',
  lime: '#A3E635',
  moss: '#65A30D',
  indigo: '#1E3A8A',
  ink: '#08202E',
};

/** The three brand stops every gradient is built from. */
export const BRAND = { c1: OCEAN.navy, c2: OCEAN.ocean, c3: OCEAN.moss };

/** Stat-card gradients, in dashboard order. */
export const STAT_ACCENTS = [
  { from: '#0C4A6E', to: '#38BDF8', glow: 'rgba(12,74,110,0.32)' },
  { from: '#0E7490', to: '#22D3EE', glow: 'rgba(14,116,144,0.30)' },
  { from: '#3F6212', to: '#A3E635', glow: 'rgba(63,98,18,0.30)' },
  { from: '#1E3A8A', to: '#60A5FA', glow: 'rgba(30,58,138,0.30)' },
];

/**
 * Status-coded stat cards. Deeper than the raw semantic palette so they sit in
 * the Ocean family, while still reading as total / waiting / done / cancelled.
 */
export const STATUS_ACCENTS = {
  total: { from: '#0C4A6E', to: '#38BDF8', glow: 'rgba(12,74,110,0.32)' },
  booked: { from: '#B45309', to: '#FBBF24', glow: 'rgba(180,83,9,0.30)' },
  confirmed: { from: '#1E3A8A', to: '#60A5FA', glow: 'rgba(30,58,138,0.30)' },
  completed: { from: '#0F766E', to: '#5EEAD4', glow: 'rgba(15,118,110,0.30)' },
  cancelled: { from: '#9F1239', to: '#FB7185', glow: 'rgba(159,18,57,0.30)' },
  no_show: { from: '#334155', to: '#94A3B8', glow: 'rgba(51,65,85,0.30)' },
};

/**
 * Stock levels borrow the status tones rather than inventing a third scale:
 * in stock reads like a completed appointment, low like a pending one, out
 * like a cancellation. Same meaning, same colour, everywhere.
 */
export const STOCK_ACCENTS = {
  total: STATUS_ACCENTS.total,
  in_stock: STATUS_ACCENTS.completed,
  low_stock: STATUS_ACCENTS.booked,
  out_of_stock: STATUS_ACCENTS.cancelled,
};

/**
 * Categorical series colours, for charts that plot one mark per hospital or
 * category. Ordered so neighbours stay distinguishable.
 */
export const CHART_SERIES = [
  OCEAN.navy,
  OCEAN.ocean,
  '#65A30D',
  OCEAN.indigo,
  OCEAN.aqua,
  '#B45309',
  '#0F766E',
  OCEAN.sky,
];

/** "Doctor Running Late" pill. */
export const ALERT = { from: '#0E7490', to: '#22D3EE', glow: 'rgba(14,116,144,0.32)' };

/**
 * Duplication. One flat cyan — the app's own primary, so a copy reads as an
 * ordinary brand action. Matching stops mark it as a solid fill, the same way
 * REMOVED does. Kept separate from ALERT, which is a gradient the "running
 * late" pill still needs.
 */
export const DUPLICATED = {
  from: OCEAN.ocean,
  to: OCEAN.ocean,
  glow: 'rgba(8,145,178,0.34)',
};

/**
 * Deletion. One flat red, no second stop — `from` and `to` match, which is
 * how the toast styles know to paint a solid fill instead of a gradient. It
 * is a brighter red than the error tone's crimson-to-rose, and the flat fill
 * against that gradient is what keeps a deletion and a failure apart.
 */
export const REMOVED = { from: '#DC2626', to: '#DC2626', glow: 'rgba(220,38,38,0.34)' };

/* ---------------------------------------------------------------------------
   Light and dark.

   Everything below reads a CSS variable rather than naming a colour, and the
   variables are defined per mode in MODE_VARS at the foot of this file. The
   split matters: a `var()` resolves in a CSS declaration (which is what `sx`
   and a React style object compile to) but NOT in an SVG presentation
   attribute, so anything handed to Recharts as `stroke`/`fill` has to stay a
   real colour. Those live in CHART_TOKENS instead, read through
   hooks/useChartTokens.

   The upshot is that the pages importing GROUND, EDGE, HOVER_BG and the rest
   needed no changes to follow the theme.
--------------------------------------------------------------------------- */

/** Page and panel ground. */
export const GROUND = 'var(--ocean-ground)';
/** Hairline borders. */
export const EDGE = 'var(--ocean-edge)';
/** Row and menu-item hover fill. */
export const HOVER_BG = 'var(--ocean-hover)';
export const CARD_SHADOW = 'var(--ocean-card-shadow)';
export const C1_GLOW = 'var(--ocean-c1-glow)';
/** Table header fill. */
export const THEAD_BG = 'var(--ocean-thead)';
/** Translucent app-bar fill. */
export const APPBAR_BG = 'var(--ocean-appbar)';

/* The three brand gradient stops. Navy reads well on a pale ground and badly
   on a dark one, so in dark mode these lift to sky/aqua/lime. Use these — not
   BRAND.c1 — anywhere the value lands in CSS. */
export const G1 = 'var(--ocean-g1)';
export const G2 = 'var(--ocean-g2)';
export const G3 = 'var(--ocean-g3)';

/** Accent washes: a soft fill, a stronger one, a track, and a border. */
export const TINT = {
  soft: 'var(--ocean-tint-soft)',
  mid: 'var(--ocean-tint-mid)',
  track: 'var(--ocean-tint-track)',
  line: 'var(--ocean-tint-line)',
  faint: 'var(--ocean-tint-faint)',
};

export const BRAND_GRADIENT = `linear-gradient(100deg, ${G1}, ${G2}, ${G3}, ${G1})`;
export const RULE_GRADIENT = `linear-gradient(90deg, ${G1}, ${G2}, ${G3})`;
export const HEADER_GRADIENT = `linear-gradient(100deg, ${G1}, ${G2})`;

/** Chart colours, which must be real values — see the note above. */
export const CHART_TOKENS = {
  light: {
    grid: 'rgba(8,35,52,0.12)',
    line: OCEAN.navy,
    areaFrom: OCEAN.navy,
  },
  dark: {
    grid: 'rgba(255,255,255,0.13)',
    line: OCEAN.sky,
    areaFrom: OCEAN.sky,
  },
};

export const reduceMotion = '@media (prefers-reduced-motion: reduce)';

export const keyframe = {
  rise: keyframes`
    from { opacity: 0; transform: translateY(18px); }
    to   { opacity: 1; transform: none; }
  `,
  slideIn: keyframes`
    from { opacity: 0; transform: translateX(-10px); }
    to   { opacity: 1; transform: none; }
  `,
  /**
   * Chat bubbles: each side enters from its own edge, so a thread reads as a
   * back-and-forth rather than a list that happens to be indented.
   */
  bubbleInLeft: keyframes`
    from { opacity: 0; transform: translate(-14px, 6px) scale(0.96); }
    to   { opacity: 1; transform: none; }
  `,
  bubbleInRight: keyframes`
    from { opacity: 0; transform: translate(14px, 6px) scale(0.96); }
    to   { opacity: 1; transform: none; }
  `,
  fadeDown: keyframes`
    from { opacity: 0; transform: translateY(-8px); }
    to   { opacity: 1; transform: none; }
  `,
  fadeIn: keyframes`
    from { opacity: 0; }
    to   { opacity: 1; }
  `,
  grow: keyframes`
    from { transform: scaleX(0); }
    to   { transform: scaleX(1); }
  `,
  /** Pans a 250%-wide gradient — the panel top rules. */
  sweep: keyframes`
    to { background-position: 250% 0; }
  `,
  /** Slow hue drift for gradient text. */
  hue: keyframes`
    0%, 100% { background-position: 0% 0; }
    50%      { background-position: 100% 0; }
  `,
  shine: keyframes`
    to { left: 130%; }
  `,
  ring: keyframes`
    0%        { opacity: 0.55; transform: scale(1); }
    70%, 100% { opacity: 0;    transform: scale(1.24); }
  `,
  blink: keyframes`
    0%, 100% { opacity: 1; }
    50%      { opacity: 0.22; }
  `,
  fill: keyframes`
    from { width: 0; }
  `,
  nudge: keyframes`
    0%, 88%, 100% { transform: none; }
    94%           { transform: translateY(-2px); }
  `,
  /** Scale-in for a value that just changed. */
  pop: keyframes`
    from { opacity: 0; transform: scale(0.6); }
    to   { opacity: 1; transform: none; }
  `,
  drift1: keyframes`
    0%, 100% { transform: none; }
    50%      { transform: translate(52px, 36px) scale(1.14); }
  `,
  drift2: keyframes`
    0%, 100% { transform: none; }
    50%      { transform: translate(-46px, 44px) scale(1.1); }
  `,
  drift3: keyframes`
    0%, 100% { transform: none; }
    50%      { transform: translate(40px, -40px) scale(1.16); }
  `,
  /** Gentle bob for a floating badge or glyph. */
  float: keyframes`
    0%, 100% { transform: translateY(0) rotate(0deg); }
    50%      { transform: translateY(-10px) rotate(4deg); }
  `,
  /** Split-screen halves entering from opposite sides. */
  panelInLeft: keyframes`
    from { opacity: 0; transform: translateX(-48px); }
    to   { opacity: 1; transform: none; }
  `,
  panelInRight: keyframes`
    from { opacity: 0; transform: translateX(48px); }
    to   { opacity: 1; transform: none; }
  `,
  /** Draws an SVG stroke (set stroke-dasharray/offset to 1 with pathLength=1). */
  trace: keyframes`
    0%   { stroke-dashoffset: 1; opacity: 0; }
    10%  { opacity: 1; }
    60%  { stroke-dashoffset: 0; opacity: 1; }
    100% { stroke-dashoffset: 0; opacity: 0; }
  `,
};

/** Text that carries the brand gradient instead of a flat colour. */
export const gradientText = {
  background: BRAND_GRADIENT,
  backgroundSize: '240% 100%',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  color: 'transparent',
  animation: `${keyframe.hue} 9s ease-in-out infinite`,
  [reduceMotion]: { animation: 'none' },
};

/** 3px animated rule along the top edge of a panel. */
/**
 * Card top accent — intentionally disabled.
 *
 * This used to paint a 3px animated gradient rule along the top edge of every
 * panel. It is kept as a no-op rather than deleted because ~50 call sites
 * apply it as `'&::before': panelRule`; neutralising it here switches the
 * accent off everywhere at once, and restoring it is a one-place change.
 */
export const panelRule = {
  display: 'none',
};

/** Section heading with a gradient underline that draws itself in. */
export const sectionTitle = {
  ...gradientText,
  position: 'relative',
  display: 'inline-block',
  pb: 0.9,
  '&::after': {
    content: '""',
    position: 'absolute',
    left: 0,
    bottom: 0,
    height: 3,
    width: '100%',
    borderRadius: 99,
    background: RULE_GRADIENT,
    transformOrigin: 'left',
    animation: `${keyframe.grow} 0.8s 0.45s cubic-bezier(.3,0,.2,1) backwards`,
    [reduceMotion]: { animation: 'none' },
  },
};

/**
 * Search / filter field chrome. Lifts on hover, and on focus a gradient bar
 * sweeps along the bottom edge while the leading icon picks up the brand
 * colour. Apply to a TextField or to a Select's FormControl.
 */
/**
 * Chrome for a Select's dropdown, passed as `MenuProps={selectMenuProps}`.
 *
 * No `position` here: the Popover paper is already absolutely positioned, so
 * overriding it drops the menu into normal flow and it stretches to the full
 * page width — a bug that shipped once already.
 */
export const selectMenuProps = {
  PaperProps: {
    sx: {
      mt: 0.5,
      borderRadius: '12px',
      border: `1px solid ${EDGE}`,
      boxShadow: CARD_SHADOW,
      // Clips the gradient rule to the rounded corners. Height is NOT capped
      // here: `overflow: hidden` plus a maxHeight on the same element clips
      // the list with no scrollbar, which silently hides every option past
      // the cut. The cap belongs on the list below, which can scroll.
      overflow: 'hidden',
      '&::before': panelRule,
    },
  },
  MenuListProps: {
    sx: {
      // Every option stays reachable — a long list scrolls inside the menu
      // instead of running off the screen.
      maxHeight: 340,
      overflowY: 'auto',
      // The scrollbar matches the app's rather than the browser default.
      scrollbarWidth: 'thin',
      '&::-webkit-scrollbar': { width: 8 },
      '&::-webkit-scrollbar-track': { background: 'transparent' },
      '&::-webkit-scrollbar-thumb': {
        background: 'rgb(var(--ocean-accent-rgb) / 0.28)',
        borderRadius: 99,
      },
      '&::-webkit-scrollbar-thumb:hover': {
        background: 'rgb(var(--ocean-accent-rgb) / 0.45)',
      },
    },
  },
};

/** A dropdown item: the gradient rail slides in from the left on hover. */
export const menuItemSx = {
  position: 'relative',
  borderRadius: '9px',
  mx: 0.75,
  my: 0.25,
  transition: 'background-color .2s, padding-left .2s',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: 6,
    bottom: 6,
    width: 3,
    borderRadius: 99,
    background: RULE_GRADIENT,
    transform: 'scaleY(0)',
    transition: 'transform .22s',
  },
  '&:hover': { backgroundColor: HOVER_BG, pl: 2.2 },
  '&:hover::before, &.Mui-selected::before': { transform: 'scaleY(1)' },
  '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: HOVER_BG },
  [reduceMotion]: { transition: 'none', '&::before': { transition: 'none' } },
};

/**
 * A dialog's paper: rounded, edged, with the animated gradient rule along the
 * top and a scale-in on open.
 */
export const dialogPaperSx = {
  // `position` is load-bearing, not tidiness: MuiPaper is statically
  // positioned, so without it the absolutely-positioned top rule below
  // resolved against the viewport and never appeared on the dialog at all.
  position: 'relative',
  borderRadius: '20px',
  overflow: 'hidden',
  // Nearly full-bleed on a phone, where MUI's 32px margin leaves a form
  // about 250px wide. `width` must be restated to match the margin, and must
  // stay a viewport calculation: leaving it `auto` lets the paper size to its
  // content, which makes the dialog visibly jump wider the moment a section
  // like a slot grid renders. Every dialog using this token passes
  // `fullWidth`, and `maxWidth` still comes from the Dialog's own prop.
  m: { xs: 1.5, sm: 4 },
  width: { xs: 'calc(100% - 24px)', sm: 'calc(100% - 64px)' },
  border: `1px solid ${EDGE}`,
  '&::before': panelRule,
  animation: `${keyframe.grow} .3s cubic-bezier(.2,.9,.3,1)`,
  [reduceMotion]: { animation: 'none' },
};

/**
 * A dialog's action bar.
 *
 * The ground and top edge are not decoration: dialog content scrolls, and
 * without them the buttons sat on top of whichever field happened to be
 * underneath.
 */
export const dialogActionsSx = {
  px: 3,
  py: 2,
  gap: 1,
  bgcolor: 'background.paper',
  borderTop: `1px solid ${EDGE}`,
};

/** A dialog's cancel button: neutral, with the same lift as its sibling. */
export const dialogCancelSx = {
  borderRadius: 99,
  px: 3,
  borderColor: EDGE,
  transition: 'border-color .2s, background-color .2s, transform .2s',
  '&:hover': {
    borderColor: BRAND.c1,
    bgcolor: HOVER_BG,
    transform: 'translateY(-1px)',
  },
  [reduceMotion]: { transition: 'none', '&:hover': { transform: 'none' } },
};

/** A dialog's confirming button: the brand gradient with a hover sweep. */
export const dialogConfirmSx = {
  borderRadius: 99,
  px: 3,
  color: '#fff',
  background: `linear-gradient(120deg, ${BRAND.c1}, ${BRAND.c2})`,
  backgroundSize: '180% 100%',
  boxShadow: `0 6px 18px ${C1_GLOW}`,
  transition: 'transform .2s, box-shadow .25s, background-position .5s',
  '&:hover': { backgroundPosition: '100% 0', transform: 'translateY(-2px)' },
  [reduceMotion]: { transition: 'none', '&:hover': { transform: 'none' } },
};

/**
 * The gradient band a list table wears as its header.
 *
 * The `& th` reset is load-bearing: the MUI theme paints head cells with their
 * own background, which would sit on top of this band and hide it.
 */
export const tableHeadSx = {
  background: HEADER_GRADIENT,
  '& th': {
    background: 'transparent',
    color: 'rgba(255,255,255,0.92)',
    fontWeight: 700,
    fontSize: '0.68rem',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    borderBottom: 0,
    whiteSpace: 'nowrap',
  },
};

/**
 * A list row: staggers in, and on hover grows an accent bar while its first
 * cell's CONTENT shifts.
 *
 * The shift is a transform on the cell's child, not padding on the cell —
 * animating padding reflows every column in the table on every hover.
 * The stagger is capped so the last row of a long page is not left waiting.
 */
export const tableRowSx = (idx = 0) => ({
  position: 'relative',
  transition: 'background-color .22s ease',
  animation: `${keyframe.slideIn} .5s cubic-bezier(.2,.9,.3,1) backwards`,
  animationDelay: `${0.2 + Math.min(idx, 10) * 0.06}s`,
  '& td:first-of-type': { position: 'relative' },
  '& td:first-of-type > *': { display: 'block', transition: 'transform .22s' },
  '& td:first-of-type::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: 3,
    background: `linear-gradient(180deg, ${BRAND.c1}, ${OCEAN.aqua})`,
    transform: 'scaleY(0)',
    transition: 'transform .24s',
  },
  '&:hover': { bgcolor: HOVER_BG },
  '&:hover td:first-of-type > *': { transform: 'translateX(7px)' },
  '&:hover td:first-of-type::before': { transform: 'scaleY(1)' },
  [reduceMotion]: {
    animation: 'none',
    '& td:first-of-type > *, & td:first-of-type::before': { transition: 'none' },
  },
});

/**
 * The card a list table sits in.
 *
 * `overflowX: auto` on the inner container is the responsive part: a wide
 * table scrolls sideways within its own card instead of pushing the whole
 * page wider than the screen, which is what made these pages unusable on a
 * phone. Cells stop wrapping to preserve the columns, so the scroll is the
 * one that gives.
 */
export const tableCardSx = {
  position: 'relative',
  overflow: 'hidden',
  border: `1px solid ${EDGE}`,
  borderRadius: '16px',
  boxShadow: CARD_SHADOW,
  '&::before': panelRule,
  '& .MuiTableContainer-root': { overflowX: 'auto' },
  '& .MuiTableCell-root': { whiteSpace: 'nowrap' },
};

/**
 * A chip that toggles a setting or a filter.
 *
 * Active chips carry their own gradient and glow so the chosen option reads at
 * a glance across a row of eight; inactive ones stay outlined and quiet. Each
 * group passes its own tone, which is what keeps the reminder schedule, the
 * delivery channel and the time filters from looking like one undifferentiated
 * wall of the same blue.
 *
 * The two branches each carry a complete `&:hover`, rather than a shared one
 * plus an override: duplicate keys in an sx object silently drop the earlier
 * value, which would have cost the inactive chips their hover entirely.
 */
export const toggleChipSx = (active, tone) => {
  const { from = BRAND.c1, to = OCEAN.ocean, glow = C1_GLOW } = tone || {};
  const base = {
    fontWeight: 700,
    borderRadius: 99,
    transition:
      'transform .22s cubic-bezier(.2,.8,.3,1), box-shadow .25s, background .25s, border-color .25s',
    [reduceMotion]: { transition: 'none', '&:hover': { transform: 'none' } },
  };
  return active
    ? {
        ...base,
        color: '#fff',
        border: 0,
        background: `linear-gradient(135deg, ${from}, ${to})`,
        boxShadow: `0 6px 16px ${glow}`,
        '& .MuiChip-icon': { color: '#fff' },
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 10px 22px ${glow}`,
        },
      }
    : {
        ...base,
        color: 'text.secondary',
        bgcolor: 'transparent',
        border: `1px solid ${EDGE}`,
        '&:hover': {
          transform: 'translateY(-2px)',
          borderColor: TINT.line,
          bgcolor: HOVER_BG,
        },
      };
};

/**
 * An on/off switch.
 *
 * The track carries the same green gradient a "completed" status wears, so
 * "this is live" reads identically whether it is a workflow, an appointment
 * or the auto-reminder setting. Shared because MUI's default `color="success"`
 * green belongs to no palette here.
 */
export const switchSx = {
  '& .MuiSwitch-track': {
    bgcolor: TINT.track,
    opacity: 1,
    transition: 'background .3s',
  },
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: '#fff',
    '&:hover': { bgcolor: 'transparent' },
  },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    opacity: 1,
    background: `linear-gradient(90deg, ${STATUS_ACCENTS.completed.from}, ${STATUS_ACCENTS.completed.to})`,
  },
  '& .MuiSwitch-thumb': { boxShadow: `0 2px 6px ${TINT.mid}` },
  [reduceMotion]: { '& .MuiSwitch-track': { transition: 'none' } },
};

/**
 * A row of filters above a table: side by side on a desktop, stacked full
 * width on a phone rather than squeezed into unusable slivers.
 */
export const filterRowSx = {
  display: 'flex',
  flexDirection: { xs: 'column', sm: 'row' },
  alignItems: { xs: 'stretch', sm: 'center' },
  gap: 1.5,
  flexWrap: 'wrap',
  mb: 3,
  '& > *': { minWidth: { sm: 180 } },
};

/**
 * Panel entry: rises into place once, after `delay` seconds.
 * `backwards` fill means the panel is already hidden before the delay runs,
 * otherwise it would flash at full opacity first.
 */
export const riseIn = (delay) => ({
  animation: `${keyframe.rise} .6s cubic-bezier(.2,.9,.3,1) backwards`,
  animationDelay: `${delay}s`,
  [reduceMotion]: { animation: 'none' },
});

/**
 * Form-field entry: fields rise in one after another. Capped at eight steps so
 * the last field of a long form is never left waiting on a long queue.
 */
export const fieldIn = (i) => ({
  animation: `${keyframe.rise} .42s cubic-bezier(.2,.9,.3,1) backwards`,
  animationDelay: `${0.06 + Math.min(i, 8) * 0.045}s`,
  [reduceMotion]: { animation: 'none' },
});

/**
 * Round table-action button: tinted glyph at rest, filling with its own
 * gradient on hover. Each action passes its own pair so edit, delete and
 * approve stay distinguishable.
 */
export const actionButtonSx = (from, to, glow) => ({
  width: 30,
  height: 30,
  color: from,
  transition:
    'transform .22s cubic-bezier(.2,.8,.3,1), background .25s, color .2s, box-shadow .25s',
  '&:hover': {
    background: `linear-gradient(135deg, ${from}, ${to})`,
    color: '#fff',
    transform: 'scale(1.12)',
    boxShadow: `0 6px 16px ${glow}`,
  },
  '&:active': { transform: 'scale(0.95)' },
  [reduceMotion]: { transition: 'none', '&:hover': { transform: 'none' } },
});

/**
 * The rounded shell the app-bar preference controls sit in (text size, theme).
 * Shared so the two stay identical twins rather than drifting apart.
 */
export const controlShellSx = {
  display: 'inline-flex',
  alignItems: 'center',
  borderRadius: 99,
  border: `1px solid ${EDGE}`,
  background: `linear-gradient(135deg, ${TINT.faint}, ${TINT.soft})`,
  transition: 'border-color .25s, box-shadow .25s',
  '&:hover': {
    borderColor: TINT.line,
    boxShadow: `0 4px 14px ${TINT.mid}`,
  },
  [reduceMotion]: { transition: 'none' },
};

export const filterFieldSx = {
  '& .MuiOutlinedInput-root': {
    position: 'relative',
    borderRadius: '12px',
    bgcolor: 'background.paper',
    transition: 'box-shadow .25s, transform .2s',
    '& fieldset': { borderColor: EDGE, transition: 'border-color .25s' },
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: `0 6px 16px ${C1_GLOW}`,
      '& fieldset': { borderColor: G2 },
    },
    // Gradient bar, clipped to the rounded corners, drawn in on focus.
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 10,
      right: 10,
      bottom: 0,
      height: 2,
      borderRadius: 99,
      background: RULE_GRADIENT,
      transform: 'scaleX(0)',
      transformOrigin: 'left',
      transition: 'transform .32s cubic-bezier(.3,0,.2,1)',
      pointerEvents: 'none',
    },
    '&.Mui-focused': {
      boxShadow: `0 8px 22px ${C1_GLOW}`,
      '& fieldset': { borderColor: G2, borderWidth: 1 },
    },
    '&.Mui-focused::after': { transform: 'scaleX(1)' },
    '& .field-glyph': { transition: 'transform .25s, color .25s' },
    '&.Mui-focused .field-glyph': { color: G2, transform: 'scale(1.12)' },
    [reduceMotion]: {
      transition: 'none',
      '&:hover': { transform: 'none' },
      '&::after': { transition: 'none' },
      '& .field-glyph': { transition: 'none' },
    },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: G1 },
};

const auraBase = {
  position: 'absolute',
  borderRadius: '50%',
  filter: 'blur(70px)',
  pointerEvents: 'none',
  zIndex: -1,
  opacity: 0.2,
  [reduceMotion]: { animation: 'none' },
};

/** Slow-drifting colour glows behind the page. Parent needs `isolation: isolate`. */
export const auras = [
  {
    ...auraBase,
    width: 360,
    height: 360,
    top: -150,
    left: -70,
    background: G1,
    animation: `${keyframe.drift1} 18s ease-in-out infinite`,
  },
  {
    ...auraBase,
    width: 320,
    height: 320,
    top: -100,
    right: -80,
    background: G2,
    animation: `${keyframe.drift2} 22s ease-in-out infinite`,
  },
  {
    ...auraBase,
    width: 340,
    height: 340,
    bottom: -190,
    left: '48%',
    background: G3,
    animation: `${keyframe.drift3} 20s ease-in-out infinite`,
  },
];

/* ---------------------------------------------------------------------------
   Sidebar (rail) palettes.

   Four complete palettes rather than one, because the rail is the largest
   block of colour on screen and the right answer is a matter of taste. Every
   preset carries the full token set, so switching is one word at RAIL below
   and nothing in the layout needs touching.
--------------------------------------------------------------------------- */
export const RAIL_PRESETS = {
  /* Midnight indigo. Cool blue-violet that ties to the theme's secondary
     navy; the cyan active pill reads strongest against it. */
  indigo: {
    bg: 'linear-gradient(178deg, #1B2550 0%, #090E24 100%)',
    text: '#A7B1D6',
    textHover: '#E7EBFA',
    group: '#7883B6',
    who: 'rgba(255,255,255,0.065)',
    whoName: '#ECEFFC',
    whoRole: '#99A4D0',
    divider: 'rgba(255,255,255,0.1)',
    hover: 'rgba(255,255,255,0.075)',
    brandText: '#fff',
    active: `linear-gradient(100deg, ${OCEAN.ocean}, rgba(34,211,238,0.62))`,
    activeText: '#fff',
    activeGlow: '0 5px 18px rgba(8,145,178,0.5)',
    edgeBar: OCEAN.aqua,
    count: 'rgba(255,255,255,0.15)',
    countText: '#D8DFF9',
    scrollThumb: 'rgba(255,255,255,0.14)',
    signOut: '#98A3CE',
    signOutText: '#FCA5A5',
    signOutBg: 'rgba(239,68,68,0.16)',
  },

  /* Deep ocean ink. Teal-black, the closest match to the Ocean palette
     itself; warmer than the slate it replaces. */
  teal: {
    bg: 'linear-gradient(178deg, #0B3A46 0%, #03181F 100%)',
    text: '#9BBAC3',
    textHover: '#DDEFF3',
    group: '#6F94A1',
    who: 'rgba(255,255,255,0.065)',
    whoName: '#E7F3F6',
    whoRole: '#8BABB5',
    divider: 'rgba(255,255,255,0.1)',
    hover: 'rgba(255,255,255,0.075)',
    brandText: '#fff',
    active: `linear-gradient(100deg, ${OCEAN.indigo}, rgba(56,189,248,0.62))`,
    activeText: '#fff',
    activeGlow: '0 5px 18px rgba(30,58,138,0.5)',
    edgeBar: OCEAN.sky,
    count: 'rgba(255,255,255,0.14)',
    countText: '#D0E7ED',
    scrollThumb: 'rgba(255,255,255,0.14)',
    signOut: '#8FAAB4',
    signOutText: '#FCA5A5',
    signOutBg: 'rgba(239,68,68,0.16)',
  },

  /* Dark slate: the rail as it was, kept so it can be put back. */
  slate: {
    bg: 'linear-gradient(178deg, #16262F 0%, #0D1920 100%)',
    text: '#9FB2BC',
    textHover: '#DCE7EC',
    group: '#6D8494',
    who: 'rgba(255,255,255,0.055)',
    whoName: '#E5EEF1',
    whoRole: '#88A0AC',
    divider: 'rgba(255,255,255,0.08)',
    hover: 'rgba(255,255,255,0.06)',
    brandText: '#fff',
    active: `linear-gradient(100deg, ${BRAND.c1}, rgba(8,145,178,0.55))`,
    activeText: '#fff',
    activeGlow: '0 5px 16px rgba(12,74,110,0.5)',
    edgeBar: OCEAN.aqua,
    count: 'rgba(255,255,255,0.14)',
    countText: '#CFE0E7',
    scrollThumb: 'rgba(255,255,255,0.12)',
    signOut: '#93A8B4',
    signOutText: '#FCA5A5',
    signOutBg: 'rgba(239,68,68,0.14)',
  },

  /* Clinical light. Inverts the rail to near-white; the lightest of the four
     and the biggest departure. Text darkens instead of lightening, so every
     foreground token flips with it. */
  light: {
    bg: 'linear-gradient(178deg, #FFFFFF 0%, #E8F2F8 100%)',
    text: '#4E6A79',
    textHover: OCEAN.ink,
    group: '#8AA3B1',
    who: 'rgba(8,145,178,0.07)',
    whoName: OCEAN.ink,
    whoRole: '#5D7785',
    divider: 'rgba(8,35,52,0.1)',
    hover: 'rgba(8,145,178,0.1)',
    brandText: OCEAN.navy,
    active: `linear-gradient(100deg, ${BRAND.c1}, ${BRAND.c2})`,
    activeText: '#fff',
    activeGlow: '0 5px 16px rgba(12,74,110,0.32)',
    edgeBar: OCEAN.ocean,
    count: 'rgba(12,74,110,0.1)',
    countText: OCEAN.navy,
    scrollThumb: 'rgba(8,35,52,0.16)',
    signOut: '#5D7785',
    signOutText: '#B91C1C',
    signOutBg: 'rgba(239,68,68,0.1)',
  },
};

/** Which preset each mode wears. Swap either side to reskin the sidebar. */
export const MODE_RAIL = {
  light: RAIL_PRESETS.light,
  dark: RAIL_PRESETS.indigo,
};

const kebab = (key) => key.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);

/* RAIL and the CSS variables are generated from the same key list, so the two
   can never drift apart when a token is added to a preset. */
const railVars = (preset) =>
  Object.fromEntries(Object.entries(preset).map(([k, v]) => [`--rail-${kebab(k)}`, v]));

/** The rail tokens the layout consumes; each one follows the active mode. */
export const RAIL = Object.fromEntries(
  Object.keys(RAIL_PRESETS.light).map((k) => [k, `var(--rail-${kebab(k)})`])
);

/**
 * The CSS variables behind every token above, one set per mode. Emitted by
 * theme.js into `:root` and `:root[data-theme='dark']`, so switching mode is
 * one attribute on <html> and the whole app repaints without a re-render.
 */
export const MODE_VARS = {
  light: {
    '--ocean-ground': 'linear-gradient(165deg, #F6FBFD 0%, #EDF5FA 100%)',
    '--ocean-edge': '#D9E5EC',
    '--ocean-hover': '#EEF7FC',
    '--ocean-card-shadow':
      '0 2px 4px rgba(8,35,52,0.05), 0 10px 24px rgba(8,35,52,0.08)',
    '--ocean-c1-glow': 'rgba(12,74,110,0.2)',
    '--ocean-thead': '#F8FAFC',
    '--ocean-appbar': 'rgba(255,255,255,0.8)',
    '--ocean-accent-rgb': '12 74 110',
    '--ocean-accent2-rgb': '8 145 178',
    '--ocean-g1': OCEAN.navy,
    '--ocean-g2': OCEAN.ocean,
    '--ocean-g3': OCEAN.moss,
    '--ocean-tint-faint': 'rgba(12,74,110,0.05)',
    '--ocean-tint-soft': 'rgba(12,74,110,0.07)',
    '--ocean-tint-mid': 'rgba(8,145,178,0.12)',
    '--ocean-tint-track': 'rgba(12,74,110,0.14)',
    '--ocean-tint-line': 'rgba(12,74,110,0.35)',
    ...railVars(MODE_RAIL.light),
  },
  dark: {
    '--ocean-ground': 'linear-gradient(165deg, #12242F 0%, #0B1821 100%)',
    '--ocean-edge': '#24363F',
    '--ocean-hover': '#182C38',
    '--ocean-card-shadow':
      '0 2px 4px rgba(0,0,0,0.36), 0 10px 24px rgba(0,0,0,0.44)',
    '--ocean-c1-glow': 'rgba(34,211,238,0.22)',
    '--ocean-thead': '#16262F',
    '--ocean-appbar': 'rgba(17,30,38,0.82)',
    '--ocean-accent-rgb': '56 189 248',
    '--ocean-accent2-rgb': '34 211 238',
    '--ocean-g1': OCEAN.sky,
    '--ocean-g2': OCEAN.aqua,
    '--ocean-g3': OCEAN.lime,
    '--ocean-tint-faint': 'rgba(56,189,248,0.08)',
    '--ocean-tint-soft': 'rgba(56,189,248,0.11)',
    '--ocean-tint-mid': 'rgba(34,211,238,0.18)',
    '--ocean-tint-track': 'rgba(56,189,248,0.2)',
    '--ocean-tint-line': 'rgba(56,189,248,0.45)',
    ...railVars(MODE_RAIL.dark),
  },
};
